import { useState, useEffect } from 'react';
import { X, AlertTriangle, AlertOctagon, Info, ExternalLink, Wind, Thermometer, CloudLightning, Cloud, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SEVERITY_CONFIG = {
  advisory: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-300',
    text: 'text-yellow-900',
    icon: Info,
    iconColor: 'text-yellow-600',
    modalHeader: 'bg-yellow-500',
    label: 'Advisory',
    pulse: '',
  },
  warning: {
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    text: 'text-orange-900',
    icon: AlertTriangle,
    iconColor: 'text-orange-500',
    modalHeader: 'bg-orange-500',
    label: 'Warning',
    pulse: 'pulse-warning',
  },
  emergency: {
    bg: 'bg-red-50',
    border: 'border-red-500',
    text: 'text-red-900',
    icon: AlertOctagon,
    iconColor: 'text-red-600',
    modalHeader: 'bg-red-600',
    label: 'Emergency Notice',
    pulse: 'pulse-emergency',
  },
};

const TYPE_ICONS = {
  'wildfire': null,
  'evacuation': AlertOctagon,
  'air-quality': Wind,
  'heat': Thermometer,
  'thunderstorm': CloudLightning,
  'weather': Cloud,
};

const PULSE_CSS = `
@keyframes pulse-red {
  0%, 100% { border-color: rgb(239,68,68); box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
  50% { border-color: rgb(185,28,28); box-shadow: 0 0 0 6px rgba(239,68,68,0); }
}
@keyframes pulse-orange {
  0%, 100% { border-color: rgb(251,146,60); box-shadow: 0 0 0 0 rgba(251,146,60,0.35); }
  50% { border-color: rgb(234,88,12); box-shadow: 0 0 0 5px rgba(251,146,60,0); }
}
.pulse-emergency { animation: pulse-red 2s ease-in-out infinite; }
.pulse-warning { animation: pulse-orange 2s ease-in-out infinite; }
`;

function SafetyModal({ alert, config, onClose }) {
  const showWindWarning = alert.type === 'wildfire' && alert.windTowardUser && alert.windSpeed;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl overflow-hidden bg-white"
        style={{ maxHeight: '80vh' }}
      >
        <div className={`${config.modalHeader} px-4 py-3 flex items-center justify-between`}>
          <p className="text-white font-bold text-base">{config.label}</p>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <p className="font-bold text-stone-900 text-lg">{alert.title}</p>
            <p className="text-stone-600 text-sm mt-1">{alert.distance}</p>
          </div>

          {showWindWarning && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl px-3 py-2.5 flex items-start gap-2">
              <Wind className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-orange-800 font-medium">
                Wind blowing toward your location at {alert.windSpeed} km/h — smoke and embers may travel quickly.
              </p>
            </div>
          )}

          <a
            href={alert.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#722F37] text-white font-semibold rounded-xl"
          >
            <ExternalLink className="w-4 h-4" />
            Tap here for more information
          </a>

          <div className="border-t border-stone-100 pt-4 space-y-2 text-sm text-stone-700">
            {alert.status && (
              <div className="flex justify-between">
                <span className="text-stone-500">Status</span>
                <span className="font-medium">{alert.status}</span>
              </div>
            )}
            {alert.size && (
              <div className="flex justify-between">
                <span className="text-stone-500">Size</span>
                <span className="font-medium">{alert.size}</span>
              </div>
            )}
            {alert.aqi && (
              <div className="flex justify-between">
                <span className="text-stone-500">Air Quality Index</span>
                <span className="font-medium">{alert.aqi}</span>
              </div>
            )}
            {alert.type === 'evacuation' && (
              <p className="text-red-700 font-medium pt-1">
                Stay informed and be prepared to leave on short notice. Follow instructions from emergency officials.
              </p>
            )}
            {alert.type === 'air-quality' && (
              <p className="text-amber-700 pt-1">
                Consider carrying any medication (inhalers, etc.) and limiting time outdoors between wineries.
              </p>
            )}
            {alert.type === 'heat' && (
              <p className="text-orange-700 pt-1">
                Stay hydrated, seek shade between wineries, and watch for signs of heat illness.
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default function SafetyBanner({ alerts, severity }) {
  const [dismissed, setDismissed] = useState(false);
  const [alertIndex, setAlertIndex] = useState(0);
  const [openAlert, setOpenAlert] = useState(null);

  // Android vibration on emergency
  useEffect(() => {
    if (severity === 'emergency' && navigator.vibrate) {
      navigator.vibrate([400, 150, 400]);
    }
  }, [severity]);

  if (dismissed || !alerts || alerts.length === 0 || severity === 'none') return null;

  const current = alerts[Math.min(alertIndex, alerts.length - 1)];
  const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.advisory;
  const TypeIcon = TYPE_ICONS[current.type];
  const Icon = TypeIcon || config.icon;
  const hasMultiple = alerts.length > 1;

  return (
    <>
      <style>{PULSE_CSS}</style>

      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${config.bg} ${config.border} border-2 rounded-xl px-3 py-2.5 flex items-center gap-2 cursor-pointer ${config.pulse}`}
        onClick={() => setOpenAlert(current)}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${config.iconColor} mt-0.5`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium flex-1 truncate ${config.text}`}>{current.banner}</p>
            {hasMultiple && (
              <span className={`text-xs font-mono flex-shrink-0 ${config.iconColor}`}>
                {alertIndex + 1}/{alerts.length}
              </span>
            )}
          </div>
          <p className={`text-xs ${config.iconColor} opacity-70`}>Tap for more details</p>
        </div>

        {hasMultiple && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); setAlertIndex(i => Math.max(0, i - 1)); }}
              className={`w-6 h-6 flex items-center justify-center rounded ${config.iconColor} hover:opacity-70`}
              disabled={alertIndex === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setAlertIndex(i => Math.min(alerts.length - 1, i + 1)); }}
              className={`w-6 h-6 flex items-center justify-center rounded ${config.iconColor} hover:opacity-70`}
              disabled={alertIndex === alerts.length - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
          className={`flex-shrink-0 ${config.iconColor} hover:opacity-70`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>

      <AnimatePresence>
        {openAlert && (
          <SafetyModal
            alert={openAlert}
            config={config}
            onClose={() => setOpenAlert(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
